// Phase 4.5 Part 21 — translate structured backend errors into plain
// employee language. Never show raw API JSON to a normal employee.
//
// Two response shapes exist from the backend (backend/server.py):
//  - `BusinessError` (services/*) -> the JSON body IS the structured error
//    itself: {code, message, object, blockers, required_action} — no
//    "detail" wrapper.
//  - a bare `HTTPException(status, "some string")` -> FastAPI's default
//    shape: {detail: "some string"}.
// This helper normalizes both so every screen can show one plain message.

const PLAIN_MESSAGES = {
  EXPOSURE_INCOMPLETE: "Financial information needs to be updated before this order can be reviewed.",
  ADVANCE_NOT_VERIFIED: "Customer payment proof was received, but Finance has not verified the funds.",
  FINANCE_HOLD_ACTIVE: "This order is on Finance Hold.",
  FINANCE_RELEASE_REQUIRED: "This order does not currently have a valid Finance release — re-check the Finance Gate result.",
  ACCEPTANCE_NOT_VERIFIED: "The customer acceptance for this order is not yet verified.",
  SCOPE_CHANGED_REQUOTE_REQUIRED: "The customer's requirement changed. A revised quotation is required.",
  DEMO_DATA_NOT_ALLOWED: "This record is built from demo/seed data and can never enter Finance review or Job creation.",
  CONCURRENT_MODIFICATION: "This record was updated by someone else. Refresh before continuing.",
  HANDOFF_VERSION_CONFLICT: "The Job handoff changed while you were reviewing it. Refresh before deciding.",
  APPROVAL_REQUIRED: "A matching Management approval is required before this can proceed.",
  UNAUTHORIZED_ACTION: "Your role is not authorized to perform this action.",
  FINANCIAL_PROFILE_REQUIRED: "This client has no financial profile on file yet.",
  INVALID_STATE: "This action isn't valid for the current state of this record.",
  EVIDENCE_REQUIRED: "A reason/note is required to complete this action.",
  // Phase 4.6 — Sales commercial workspace codes.
  INQUIRY_INCOMPLETE: "Customer information is still missing.",
  RATE_EXPIRED: "The selected supplier rate has expired. Request new rates before continuing.",
  QUOTATION_APPROVAL_REQUIRED: "This quotation must be approved before it can be sent.",
  QUOTATION_VERSION_MISMATCH: "A newer quotation version exists.",
  QUOTATION_EXPIRED: "This quotation has expired. A revision or re-rate is required before it can be sent.",
  // Phase 4.7 — commercial outcome closure.
  OPPORTUNITY_ALREADY_CLOSED: "This opportunity is already closed.",
  LOSS_REASON_REQUIRED: "Please choose a reason before closing this opportunity.",
  NO_RESPONSE_POLICY_NOT_MET: "Not enough contact attempts have been logged yet — log more attempts before closing this as No Response.",
  VERIFIED_ACCEPTANCE_CANNOT_BE_CLOSED: "This order already has a verified customer acceptance and can't be closed as Lost from here.",
  DOWNSTREAM_PROCESS_EXISTS: "This order has already progressed past Sales (a Job exists) and can't be closed from here.",
  REACTIVATION_NOT_ALLOWED: "This opportunity can't be reactivated in its current state.",
  DUPLICATE_INQUIRY_ALREADY_LINKED: "This opportunity was already reactivated as a new inquiry.",
};

export function apiErrorCode(e) {
  const data = e?.response?.data;
  if (!data) return null;
  if (typeof data.code === "string") return data.code;
  return null;
}

export function apiErrorMessage(e, fallback = "Something went wrong. Please try again.") {
  const data = e?.response?.data;
  if (!data) return e?.message || fallback;
  const code = apiErrorCode(e);
  if (code && PLAIN_MESSAGES[code]) return PLAIN_MESSAGES[code];
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;
  return fallback;
}

export function isConflict(e) {
  const code = apiErrorCode(e);
  return e?.response?.status === 409 || code === "CONCURRENT_MODIFICATION" || code === "HANDOFF_VERSION_CONFLICT" || code === "FINANCE_RELEASE_REQUIRED";
}

// Narrower than isConflict(): true only for a genuine stale-`version` race
// (retrying with a freshly-refetched version can actually resolve these).
// A plain HTTP 409 can also mean a real, non-retriable business rule was
// rejected (e.g. SCOPE_CHANGED_REQUOTE_REQUIRED, FINANCE_RELEASE_REQUIRED) —
// retrying those just reproduces the same rejection and hides the real
// reason from the user, so callers that want to auto-retry a version clash
// specifically (rather than surface the real error) should check this
// instead of isConflict().
export function isVersionConflict(e) {
  const code = apiErrorCode(e);
  return code === "CONCURRENT_MODIFICATION" || code === "HANDOFF_VERSION_CONFLICT";
}
